import { useState } from "react";
import { KeyRound, Loader2, Mail, Package } from "lucide-react";

import { api } from "@/api/client";

/**
 * Resetting a forgotten password.
 *
 * Two steps on one screen: ask for a code, then use it. Keeping them together
 * means the address is already filled in when the code arrives, and the person
 * never has to navigate anywhere with a code held in their head.
 */
export default function ForgotPassword({ onDone }) {
  const [stage, setStage] = useState("request");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const requestCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await api.forgotPassword(email.trim());
      setNotice(result.message);
      setStage("reset");
    } catch (err) {
      setError(err.message || "Could not send a reset code.");
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();

    if (password !== confirm) {
      setError("The two passwords do not match.");
      return;
    }

    setBusy(true);
    setError("");
    try {
      await api.resetPassword({ email: email.trim(), code: code.trim(), password });
      setNotice("");
      setStage("done");
    } catch (err) {
      setError(err.message || "Could not reset the password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="signin-shell">
      <div className="signin-card">
        <div className="signin-brand">
          <Package size={32} />
          <div>
            <h1>EDDM Organizer</h1>
            <p>Reset your password</p>
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {notice && <div className="alert alert-success">{notice}</div>}

        {stage === "request" && (
          <form onSubmit={requestCode}>
            <div className="form-group">
              <label className="form-label">Your email address</label>
              <input
                className="form-input"
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="btn btn-primary signin-submit" disabled={busy}>
              {busy ? <Loader2 size={16} className="spin" /> : <Mail size={16} />}
              {busy ? "Sending..." : "Email me a code"}
            </button>
          </form>
        )}

        {stage === "reset" && (
          <form onSubmit={submitReset}>
            <div className="form-group">
              <label className="form-label">Six-digit code</label>
              <input
                className="form-input reset-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">New password</label>
              <input
                className="form-input"
                type="password"
                autoComplete="new-password"
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">New password again</label>
              <input
                className="form-input"
                type="password"
                autoComplete="new-password"
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="btn btn-primary signin-submit" disabled={busy}>
              {busy ? <Loader2 size={16} className="spin" /> : <KeyRound size={16} />}
              {busy ? "Setting..." : "Set my password"}
            </button>
            <p className="signin-help">
              The code expires 15 minutes after it was sent.{" "}
              <button type="button" className="link-button" onClick={() => setStage("request")}>
                Send another
              </button>
            </p>
          </form>
        )}

        {stage === "done" && (
          <>
            <div className="alert alert-success">
              Your password has been changed. You can sign in with it now.
            </div>
            <button className="btn btn-primary signin-submit" onClick={onDone}>
              Back to sign in
            </button>
          </>
        )}

        {stage !== "done" && (
          <p className="signin-help">
            <button type="button" className="link-button" onClick={onDone}>
              Back to sign in
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
